import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Breadcrumb from "../../components/Breadcrumb/Breadcrumb";
import SearchBar from "../../components/SearchBar/SearchBar";
import CategoryFilter from "../../components/CategoryFilter/CategoryFilter";
import ProductGrid from "../../components/ProductGrid/ProductGrid";
import Pagination from "../../components/Pagination/Pagination";
import LoadingSkeleton from "../../components/LoadingSkeleton/LoadingSkeleton";
import SectionTitle from "../../components/ui/SectionTitle";
import { getCategories } from "../../services/categoryService";
import { searchProducts } from "../../services/productService";

const pageSize = 12;

export default function Products() {
  const [params, setParams] = useSearchParams();
  const [query, setQuery] = useState(params.get("q") || "");
  const [category, setCategory] = useState(params.get("categories") || params.get("category") || "all");
  const [sort, setSort] = useState("featured");
  const [page, setPage] = useState(1);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const next = {};
    if (query) next.q = query;
    if (category !== "all") {
      if (category.includes(",")) next.categories = category;
      else next.category = category;
    }
    setParams(next, { replace: true });
    setPage(1);
  }, [query, category, setParams]);

  useEffect(() => {
    getCategories().then(setCategories).catch(() => setCategories([]));
  }, []);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    searchProducts({ query, category, sort })
      .then((result) => {
        if (alive) setProducts(result);
      })
      .catch(() => {
        if (alive) setProducts([]);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [query, category, sort]);

  const totalPages = Math.ceil(products.length / pageSize);
  const visibleProducts = products.slice((page - 1) * pageSize, page * pageSize);
  const recent = products.slice(0, 4);

  return (
    <section className="container-pad py-12">
      <Breadcrumb items={[{ label: "Products" }]} />
      <SectionTitle eyebrow="Product catalogue" title="Search, filter and shortlist EV products" description="Browse EV battery products with specification-led product cards and quote actions." />
      <div className="mb-8 grid gap-4 rounded-xl border border-line bg-white p-4 shadow-soft lg:grid-cols-[1fr_220px_180px]">
        <SearchBar value={query} onChange={setQuery} />
        <CategoryFilter categories={categories} value={category.includes(",") ? "all" : category} onChange={setCategory} />
        <label>
          <span className="sr-only">Sort products</span>
          <select value={sort} onChange={(event) => setSort(event.target.value)} className="w-full rounded-xl border border-line bg-white px-4 py-3 text-sm font-bold text-dark">
            <option value="featured">Sort by Availability</option>
            <option value="low">Lowest Unit Cost</option>
            <option value="high">Highest Unit Cost</option>
          </select>
        </label>
      </div>
      {loading ? <LoadingSkeleton /> : <ProductGrid products={visibleProducts} />}
      <Pagination page={page} totalPages={totalPages} onChange={setPage} />
      <div className="mt-14">
        <SectionTitle eyebrow="Recently viewed" title="Recently viewed products" description="A quick shortlist based on your current product results." />
        <ProductGrid products={recent} />
      </div>
    </section>
  );
}
